/**
 * Composant WelcomeMessage
 *
 * Affiche un message de bienvenue personnalisé avec le logo de l'établissement
 * en haut du widget de réservation. Ce message crée une atmosphère accueillante
 * et permet à l'établissement de communiquer avec ses clients dès l'ouverture du widget.
 *
 * @module packages.common.components.table-reservation.components.WelcomeMessage
 */

import React from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import {Box, Card, CardContent} from '@mui/material';
import {typography} from 'packages/common/theme/typography';

/**
 * Composant de message de bienvenue
 *
 * Affiche un message personnalisé avec le logo de l'établissement dans une carte élégante.
 * Si aucun message n'est fourni ou si le message est vide, le composant ne s'affiche pas.
 *
 * @param {Object} props - Props du composant
 * @param {string} props.message - Message de bienvenue à afficher (max 300 caractères de texte pur)
 * @param {boolean} props.showLogo - Afficher ou non le logo de l'établissement
 * @param {string} props.companyLogo - URL du logo de l'établissement
 * @param {string} props.companyName - Nom de l'établissement
 * @return {JSX.Element|null} Composant de message de bienvenue ou null si pas de message
 */
export default function WelcomeMessage({message, showLogo, companyLogo, companyName}) {
  // Ne rien afficher si ni message ni logo à afficher
  const hasMessage = message && message.trim() !== '';
  const hasLogo = showLogo && companyLogo;

  if (!hasMessage && !hasLogo) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        mb: 3,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Card
        elevation={0}
        sx={{
          width: '100%',
          backgroundColor: 'grey.50',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <CardContent
          sx={{
            'display': 'flex',
            'flexDirection': 'column',
            'alignItems': 'center',
            'textAlign': 'center',
            'py': 2,
            'px': 2,
            '&:last-child': {
              pb: 2,
            },
          }}
        >
          {/* Logo de l'établissement */}
          {showLogo && companyLogo && (
            <Box
              sx={{
                'mb': 1.5,
                'display': 'flex',
                'alignItems': 'center',
                'justifyContent': 'center',
              }}
            >
              <Box
                component="img"
                src={companyLogo}
                alt={`Logo ${companyName || 'établissement'}`}
                sx={{
                  'maxWidth': '100px',
                  'maxHeight': '50px',
                  'objectFit': 'contain',
                }}
              />
            </Box>
          )}

          {/* Message de bienvenue */}
          <Box
            sx={{
              'color': 'text.primary',
              'lineHeight': 1.5,
              'fontSize': typography.body2.fontSize,
              'fontWeight': 400,
              'maxWidth': '100%',
              'wordBreak': 'break-word',
              '& p': {
                margin: 0,
                marginBottom: 0.125,
                fontSize: typography.body2.fontSize,
                lineHeight: 1.5,
              },
              '& p:last-child': {
                marginBottom: 0,
              },
              '& strong': {
                fontWeight: 600,
              },
              '& b': {
                fontWeight: 600,
              },
              '& em': {
                fontStyle: 'italic',
              },
              '& i': {
                fontStyle: 'italic',
              },
              '& u': {
                textDecoration: 'underline',
              },
              '& ul, & ol': {
                margin: 0,
                marginBottom: 0.125,
                paddingLeft: 2,
              },
              '& ul:last-child, & ol:last-child': {
                marginBottom: 0,
              },
              '& li': {
                fontSize: typography.body2.fontSize,
                lineHeight: 1.5,
              },
              '& a': {
                color: 'primary.main',
                textDecoration: 'underline',
              },
            }}
            dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(message)}}
          />
        </CardContent>
      </Card>
    </Box>
  );
}

WelcomeMessage.propTypes = {
  message: PropTypes.string,
  showLogo: PropTypes.bool,
  companyLogo: PropTypes.string,
  companyName: PropTypes.string,
};

WelcomeMessage.defaultProps = {
  message: '',
  showLogo: true,
  companyLogo: null,
  companyName: '',
};
